// Game sounds, synthesized with the Web Audio API — no asset files, so nothing
// to fetch (which keeps us COEP-friendly like the rest of the app) and no
// binary blobs in the repo. Short, soft blips in the spirit of lichess.
//
// The AudioContext is created lazily on first use and resumed on a user gesture
// (browsers suspend it until then). A mute toggle persists in localStorage.

type Sound = "move" | "capture" | "check" | "low" | "end" | "start";

const MUTE_KEY = "chess:muted";

let ctx: AudioContext | null = null;
let muted = false;
try { muted = localStorage.getItem(MUTE_KEY) === "1"; } catch { /* memory only */ }

export function isMuted(): boolean {
  return muted;
}

export function setMuted(v: boolean): void {
  muted = v;
  try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch { /* memory only */ }
  window.dispatchEvent(new Event("soundchange"));
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

function audio(): AudioContext | null {
  if (muted) return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  // Resume if a prior gesture left it suspended.
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** One short enveloped tone. */
function blip(freq: number, when: number, dur: number, gain: number, type: OscillatorType = "sine"): void {
  const ac = ctx;
  if (!ac) return;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = ac.currentTime + when;
  // Fast attack, exponential-ish decay via linear ramp to near-zero.
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
  g.gain.linearRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Play a named sound (no-op when muted or audio is unavailable). */
export function play(sound: Sound): void {
  if (!audio()) return;
  switch (sound) {
    case "move":
      blip(440, 0, 0.07, 0.18, "triangle");
      break;
    case "capture":
      // A lower, slightly grittier thud.
      blip(220, 0, 0.09, 0.22, "sawtooth");
      blip(160, 0.02, 0.09, 0.14, "sine");
      break;
    case "check":
      blip(660, 0, 0.08, 0.2, "square");
      blip(880, 0.08, 0.09, 0.16, "square");
      break;
    case "low":
      // Urgent double-beep for the low-time warning.
      blip(1000, 0, 0.09, 0.22, "square");
      blip(1000, 0.14, 0.09, 0.22, "square");
      break;
    case "start":
      blip(523, 0, 0.09, 0.18, "triangle");
      blip(784, 0.09, 0.11, 0.18, "triangle");
      break;
    case "end":
      // Descending three-note flourish.
      blip(660, 0, 0.12, 0.2, "triangle");
      blip(523, 0.12, 0.12, 0.2, "triangle");
      blip(392, 0.24, 0.2, 0.2, "triangle");
      break;
  }
}
